import React, { useState, useEffect } from 'react';
import { Search, Plus, Download, Upload, Eye, Trash2, X, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { apiClient } from '../../utils/apiHelper';

const PostalExchange = () => {
  const [postalRecords, setPostalRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deliveredTo, setDeliveredTo] = useState('');
  const [formData, setFormData] = useState({
    postal_date: new Date().toISOString().split('T')[0],
    postal_type: 'Incoming',
    tracking_number: '',
    sender_name: '',
    sender_address: '',
    recipient_name: '',
    recipient_department: '',
    item_type: 'Letter',
    description: '',
    received_by: '',
    status: 'Received'
  });

  useEffect(() => { fetchPostalRecords(); }, []);

  const fetchPostalRecords = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/frontdesk/postal');
      setPostalRecords(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching postal records:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/frontdesk/postal', formData);
      alert('Postal record added successfully!');
      setShowModal(false);
      resetForm();
      fetchPostalRecords();
    } catch (error) {
      alert('Failed to add postal record');
    }
  };

  const handleDeliver = async () => {
    if (!deliveredTo.trim()) {
      alert('Please enter delivered to name');
      return;
    }
    try {
      await apiClient.put(`/api/frontdesk/postal/${selectedRecord.id}/deliver?delivered_to=${encodeURIComponent(deliveredTo)}`);
      alert('Marked as delivered!');
      setShowDeliverModal(false);
      setDeliveredTo('');
      setSelectedRecord(null);
      fetchPostalRecords();
    } catch (error) {
      alert('Failed to mark as delivered');
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this postal record?')) return;
    try {
      await apiClient.delete(`/api/frontdesk/postal/${recordId}`);
      fetchPostalRecords();
    } catch (error) {
      alert('Failed to delete postal record');
    }
  };

  const resetForm = () => {
    setFormData({
      postal_date: new Date().toISOString().split('T')[0],
      postal_type: 'Incoming',
      tracking_number: '',
      sender_name: '',
      sender_address: '',
      recipient_name: '',
      recipient_department: '',
      item_type: 'Letter',
      description: '',
      received_by: '',
      status: 'Received'
    });
  };

  const filteredRecords = postalRecords.filter(r => {
    const matchesSearch = 
      r.sender_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.recipient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.tracking_number?.includes(searchTerm);
    const matchesType = filterType === 'all' || r.postal_type?.toLowerCase() === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Postal Exchange</CardTitle>
          <Button onClick={() => setShowModal(true)}>
            <Plus className="h-4 w-4 mr-2" /> Add Postal Record
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input
                type="text"
                placeholder="Search by tracking number, sender, or recipient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-36"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="incoming">Incoming</SelectItem>
                <SelectItem value="outgoing">Outgoing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Tracking No.</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">From</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">To</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Item Type</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left p-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center p-8 text-gray-500">
                      {loading ? 'Loading...' : 'No postal records found. Click "Add Postal Record" to create a new entry.'}
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((record) => (
                    <tr key={record.id} className="hover:bg-gray-50">
                      <td className="p-3 text-sm">{record.postal_date}</td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          {record.postal_type === 'Incoming' ? (
                            <Download className="h-4 w-4 text-green-600" />
                          ) : (
                            <Upload className="h-4 w-4 text-blue-600" />
                          )}
                          <span className={record.postal_type === 'Incoming' ? 'text-green-600' : 'text-blue-600'}>
                            {record.postal_type}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-sm">{record.tracking_number || '-'}</td>
                      <td className="p-3 text-sm">{record.sender_name}</td>
                      <td className="p-3 text-sm">{record.recipient_name}</td>
                      <td className="p-3 text-sm">{record.item_type}</td>
                      <td className="p-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          record.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                          record.status === 'In Transit' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {record.status}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex gap-2">
                          {record.status !== 'Delivered' && (
                            <button 
                              onClick={() => { setSelectedRecord(record); setShowDeliverModal(true); }} 
                              className="text-green-600 hover:text-green-800"
                              title="Mark Delivered"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                          )}
                          <button onClick={() => handleDelete(record.id)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add Postal Record Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add Postal Record</h2>
              <button onClick={() => setShowModal(false)}><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input type="date" required value={formData.postal_date} onChange={(e) => setFormData({...formData, postal_date: e.target.value})} />
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select value={formData.postal_type} onValueChange={(v) => setFormData({...formData, postal_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Incoming">Incoming</SelectItem>
                      <SelectItem value="Outgoing">Outgoing</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tracking Number</Label>
                  <Input value={formData.tracking_number} onChange={(e) => setFormData({...formData, tracking_number: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Sender Name *</Label>
                  <Input required value={formData.sender_name} onChange={(e) => setFormData({...formData, sender_name: e.target.value})} />
                </div>
                <div>
                  <Label>Recipient Name *</Label>
                  <Input required value={formData.recipient_name} onChange={(e) => setFormData({...formData, recipient_name: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Recipient Department</Label>
                  <Input value={formData.recipient_department} onChange={(e) => setFormData({...formData, recipient_department: e.target.value})} />
                </div>
                <div>
                  <Label>Item Type *</Label>
                  <Select value={formData.item_type} onValueChange={(v) => setFormData({...formData, item_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Letter">Letter</SelectItem>
                      <SelectItem value="Parcel">Parcel</SelectItem>
                      <SelectItem value="Document">Document</SelectItem>
                      <SelectItem value="Courier">Courier</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Input value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
              </div>
              <div>
                <Label>Received By</Label>
                <Input value={formData.received_by} onChange={(e) => setFormData({...formData, received_by: e.target.value})} />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button type="submit">Add Record</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mark Delivered Modal */}
      {showDeliverModal && selectedRecord && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Mark as Delivered</h2>
              <button onClick={() => { setShowDeliverModal(false); setSelectedRecord(null); setDeliveredTo(''); }}><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">Item: {selectedRecord.item_type} from {selectedRecord.sender_name}</p>
            <div>
              <Label>Delivered To *</Label>
              <Input 
                value={deliveredTo}
                onChange={(e) => setDeliveredTo(e.target.value)}
                placeholder="Enter recipient name..."
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button type="button" variant="outline" onClick={() => { setShowDeliverModal(false); setSelectedRecord(null); setDeliveredTo(''); }}>Cancel</Button>
              <Button onClick={handleDeliver}>Mark Delivered</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostalExchange;
